import DOMPurify from 'dompurify';
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  watch,
  type Ref,
} from 'vue';
import type { ProjectNotesProps } from '../types';
import { createProjectNotesStore } from './createProjectNotesStore';

const ALLOWED_TAGS = ['p', 'b', 'i', 'u', 'ol', 'ul', 'li', 'br', 'div'];
function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR: [] });
}

const DOMAIN_SPECIFIC_DEFAULT_NOTES =
  '<p><b>Mandat</b>: </p>' +
  '<p><b>Skyddsronder</b>: </p>' +
  '<p><b>Tavelmöten</b>: </p>' +
  '<p><b>Byggmöten</b>: </p>' +
  '<p><b>Ekonomimöten</b>: </p>' +
  '<p><b>Samordningsmöten</b>: </p>' +
  '<p><b>Möte Branddokk</b>: </p>' +
  '<p><b>Brandsyn</b>: </p>' +
  '<p><b>Driftgenomgång</b>: </p>' +
  '<p><b>HG-syn</b>: </p>' +
  '<p><b>Försyn</b>: </p>' +
  '<p><b>Myndighetssyn</b>: </p>';

export function useProjectNotesLogic(
  props: ProjectNotesProps,
  emit: (event: 'save', notes: string) => void,
) {
  const { state: storeState, dispatch } = createProjectNotesStore(
    props.initialNotes ?? DOMAIN_SPECIFIC_DEFAULT_NOTES,
  );

  watch(
    () => props.initialNotes,
    (newValue) => {
      if (storeState.isEditing) return;
      dispatch({
        type: 'update-notes',
        value: sanitize(newValue ?? DOMAIN_SPECIFIC_DEFAULT_NOTES),
      });
    },
  );
  const editorRef: Ref<HTMLDivElement | null> = ref(null);

  onMounted(() => {
    if (editorRef.value) {
      editorRef.value.innerHTML = storeState.notes ?? '';
    }
  });

  watch(
    () => storeState.isEditing,
    async (editing) => {
      if (editing) {
        await nextTick();
        if (editorRef.value) {
          editorRef.value.innerHTML = storeState.notes ?? '';
        }
      }
    },
  );

  function toggleEdit() {
    if (!storeState.isEditing) {
      dispatch({ type: 'start-edit' });
    } else {
      dispatch({ type: 'confirm-edit' });
      emit('save', storeState.notes);
    }
  }

  function cancel() {
    dispatch({ type: 'cancel-edit' });
  }

  function onInput() {
    dispatch({
      type: 'update-notes',
      value: sanitize(editorRef.value?.innerHTML ?? ''),
    });
  }

  function formatText(tag: 'b' | 'i' | 'u') {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.value?.contains(range.commonAncestorContainer)) return;

    const ancestor = range.commonAncestorContainer;
    const wrapper =
      ancestor.nodeType === Node.ELEMENT_NODE
        ? (ancestor as Element).closest(tag)
        : (ancestor.parentElement?.closest(tag) ?? null);

    if (wrapper) {
      const plain = document.createTextNode(wrapper.textContent ?? '');
      wrapper.replaceWith(plain);
    } else {
      const el = document.createElement(tag);
      el.textContent = range.toString();
      range.deleteContents();
      range.insertNode(el);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    dispatch({ type: 'update-notes', value: editorRef.value?.innerHTML ?? '' });
  }

  function insertList(type: 'ol' | 'ul') {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.value?.contains(range.commonAncestorContainer)) return;
    const list = document.createElement(type);
    const item = document.createElement('li');
    item.textContent = range.toString() || 'Listobjekt';
    list.appendChild(item);
    range.deleteContents();
    range.insertNode(list);
    editorRef.value?.focus();
    dispatch({ type: 'update-notes', value: editorRef.value?.innerHTML ?? '' });
  }

  function removeFormatting() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    if (!editorRef.value?.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    if (!editorRef.value.contains(range.commonAncestorContainer)) return;
    const plain = document.createTextNode(range.toString());
    range.deleteContents();
    range.insertNode(plain);
    dispatch({ type: 'update-notes', value: editorRef.value?.innerHTML ?? '' });
  }

  return {
    state: reactive({
      notes: computed(() => storeState.notes),
      isEditing: computed(() => storeState.isEditing),
      mode: computed(() => props.mode ?? 'read-only'),
    }),
    editorRef,
    action: {
      toggleEdit,
      cancel,
      onInput,
      formatText,
      insertList,
      removeFormatting,
    },
  };
}
